import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'src/components/ui/badge';
import { TableCell, TableRow } from 'src/components/ui/table';
import { ProductImage } from 'src/features/novelties/components/ProductImage';
import { SimilarProductAttributes } from 'src/features/novelties/components/SimilarProductAttributes';
import type { SimilarityData } from 'src/services/api/getNoveltiesBestCandidates';

function formatPrice(
  price: number | null,
  currency: string | null,
  locale: string,
): string | null {
  if (price === null || !currency || currency === 'NON DEFINI') {
    return null;
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(price);
}

function formatSellingPrice(
  price: number | null,
  locale: string,
): string | null {
  if (price === null) {
    return null;
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(price);
}

type SimilarProductRowProps = {
  similarity: SimilarityData;
};

export function SimilarProductRow({
  similarity,
}: SimilarProductRowProps): ReactElement {
  const { i18n } = useTranslation();

  const formattedPurchasePrice = formatPrice(
    similarity.purchase_price,
    similarity.purchase_currency,
    i18n.language,
  );

  const formattedSellingPrice = formatSellingPrice(
    similarity.selling_price,
    i18n.language,
  );

  const getSimilarityBadgeVariant = (score: number) => {
    if (score >= 0.8) return 'default';
    if (score >= 0.5) return 'secondary';
    if (score >= 0.2) return 'warning';
    if (score < 0.2) return 'destructive';
    return 'outline';
  };

  return (
    <TableRow>
      <TableCell className='w-12'>
        <ProductImage
          alt={similarity.title || similarity.sku}
          size='sm'
          src={similarity.image_url}
        />
      </TableCell>
      <TableCell className='font-mono text-sm'>{similarity.sku}</TableCell>
      <TableCell className='max-w-xs truncate text-sm'>
        {similarity.title || '-'}
      </TableCell>
      <TableCell className='text-center'>
        <Badge
          className='font-mono'
          variant={getSimilarityBadgeVariant(
            similarity.cosine_similarity_score,
          )}
        >
          {(similarity.cosine_similarity_score * 100).toFixed(1)}%
        </Badge>
      </TableCell>
      <TableCell className='text-center font-medium'>
        {similarity.candidate_avg_validated_forecast.toLocaleString()}
      </TableCell>
      <TableCell className='text-right font-medium'>
        {formattedPurchasePrice ?? '-'}
      </TableCell>
      <TableCell className='text-right font-medium'>
        {formattedSellingPrice ?? '-'}
      </TableCell>
      <TableCell>
        <SimilarProductAttributes similarity={similarity} />
      </TableCell>
    </TableRow>
  );
}
